import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import styled from "styled-components";
import {
  DateTableCell,
  ReactTableComponent,
  Status,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import api from "../../../../util/api";
import { AdvancedSearch } from "../../../../components/advanced-search/AdvancedSearch";
import {
  ISearchFieldConfig,
  SearchFieldType,
} from "../../../../components/advanced-search/AdvancedSearch.interface";

// Same AdvancedSearch as every other list; the endpoint already understands
// searchKeyword (reference / customer email / customer name), status, from/to.
const consentsSearchConfig: ISearchFieldConfig[] = [
  {
    type: SearchFieldType.DROPDOWN,
    label: "Status",
    key: "status",
    dropdownValues: [
      { label: "All", value: "" },
      { label: "Active", value: "ACTIVE" },
      { label: "Awaiting auth", value: "AWAITING_AUTH" },
      { label: "Expired", value: "EXPIRED" },
      { label: "Cancelled", value: "CANCELLED" },
      { label: "Rejected", value: "REJECTED" },
      { label: "Failed", value: "FAILED" },
    ],
  },
  {
    type: SearchFieldType.DATE_RANGE,
    label: "Created date",
    key: "from",
    rangeEndKey: "to",
    placeholder: "From",
    rangeEndPlaceholder: "To",
  },
];

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
`;

const CountLine = styled(Typography)`
  color: #6b7280;
  margin-top: -8px;
`;

interface ConsentRow {
  id: string;
  reference?: string;
  status: string;
  scopeMode: string;
  customer?: { fullName?: string; email?: string };
  provider?: { name: string; externalId: string };
  accounts: any[];
  consentGrantedAt?: string;
  consentExpiresAt?: string;
  lastError?: { message?: string };
  createdAt: string;
}

const statusMap: Record<
  string,
  "completed" | "pending" | "rejected" | "cancelled"
> = {
  ACTIVE: "completed",
  RECONSENT_DUE: "pending",
  AWAITING_AUTH: "pending",
  DRAFT: "pending",
  EXPIRED: "cancelled",
  CANCELLED: "cancelled",
  REJECTED: "rejected",
  FAILED: "rejected",
};

export const CompanyAisConsentsTab: React.FC<{ companyId: string }> = ({
  companyId,
}) => {
  const [data, setData] = useState<ConsentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [pagination, setPagination] = useState({ page: 1, limit: 20 });
  const [totalDocs, setTotalDocs] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getAisConsentsForCompany(companyId, {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });
      setData(result?.docs ?? []);
      setTotalDocs(result?.totalDocs ?? 0);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, pagination, JSON.stringify(filters)]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: ColumnDef<ConsentRow, any>[] = useMemo(
    () => [
      {
        accessorKey: "reference",
        header: "Reference",
        cell: ({ getValue }) => getValue() || "—",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue() as string;
          return (
            <Status type={statusMap[status] || "pending"}>
              {status.replace(/_/g, " ")}
            </Status>
          );
        },
      },
      {
        accessorKey: "provider",
        header: "Provider",
        cell: ({ getValue }) => {
          const p = getValue() as ConsentRow["provider"];
          return p?.name || "—";
        },
      },
      {
        accessorKey: "scopeMode",
        header: "Scope",
        cell: ({ getValue }) =>
          getValue() === "FULL_ACCESS" ? "Full access" : "Details only",
      },
      {
        accessorKey: "customer",
        header: "Customer",
        cell: ({ getValue }) => {
          const c = getValue() as ConsentRow["customer"];
          return c?.fullName || c?.email || "—";
        },
      },
      {
        accessorKey: "accounts",
        header: "Accounts",
        cell: ({ getValue }) => {
          const arr = getValue();
          return Array.isArray(arr) ? arr.length : 0;
        },
      },
      {
        accessorKey: "consentGrantedAt",
        header: "Granted",
        cell: ({ getValue }) =>
          getValue() ? <DateTableCell date={getValue()} /> : "—",
      },
      {
        accessorKey: "lastError",
        header: "Last error",
        cell: ({ getValue }) => {
          const err = getValue() as ConsentRow["lastError"];
          return err?.message ? (
            <Typography
              variant={TypographyVariant.BODY7}
              style={{ color: "#c43d3d", maxWidth: 200 }}
              title={err.message}
            >
              {err.message.length > 60
                ? err.message.slice(0, 60) + "…"
                : err.message}
            </Typography>
          ) : (
            "—"
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: ({ getValue }) => <DateTableCell date={getValue()} />,
      },
    ],
    []
  );

  return (
    <Wrap>
      <AdvancedSearch
        config={consentsSearchConfig}
        restoredValues={filters}
        searchPlaceholder="Search by reference, customer name or email"
        onChange={(next) => {
          setFilters(next ?? {});
          // Keep the same object when already on page 1 — a new one would
          // trigger a second fetch (AdvancedSearch re-emits once on mount).
          setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
        }}
      />
      <CountLine variant={TypographyVariant.BODY7}>
        {totalDocs} consent{totalDocs !== 1 ? "s" : ""}
      </CountLine>

      <ReactTableComponent
        data={data}
        columns={columns}
        loading={loading}
        emptyMessage="No AIS consents for this company"
      />
    </Wrap>
  );
};
